import React, { useState, useEffect } from 'react';
import RichTextContent from '../components/RichTextContent';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { companyApi, brandsApi, meshNewsApi, landingPageApi } from '../services/api';

const HomePage = () => {
  const [company, setCompany] = useState(null);
  const [brands, setBrands] = useState([]);
  const [news, setNews] = useState([]);
  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, brandsRes, newsRes, landingPageRes] = await Promise.all([
          companyApi.get(),
          brandsApi.getAll(),
          meshNewsApi.getAll(),
          landingPageApi.get(),
        ]);
        setCompany(companyRes.data);
        setBrands(brandsRes.data);
        setNews(newsRes.data);
        setLandingPage(landingPageRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount — API imports are stable module-level refs

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Use landing page hero image, fallback to first brand's hero image
  const heroImage = landingPage?.heroImage || brands[0]?.heroImage || '';

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 bg-gray-900">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-40"
            style={{
              backgroundImage: `url(${heroImage})`
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight">
              {landingPage?.heroTitle || 'Architectural'}
              <span className="block font-medium">{landingPage?.heroTitleHighlight || 'Lighting Excellence'}</span>
            </h1>
            <p className="text-xl text-gray-300 mb-10 max-w-xl leading-relaxed">
              {landingPage?.heroSubtitle || company?.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={landingPage?.ctaButtonLink || '/about'}
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-medium rounded-sm hover:bg-gray-100 transition-colors"
              >
                {landingPage?.ctaButtonText || 'Discover More'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to={landingPage?.secondaryButtonLink || '/contact'}
                className="inline-flex items-center px-8 py-4 border border-white/30 text-white font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                {landingPage?.secondaryButtonText || 'Get in Touch'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">Our Brands</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Representing world-leading architectural lighting manufacturers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to={`/brand/${brand.id}`}
                className="group relative overflow-hidden rounded-lg aspect-[16/10] cursor-pointer"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{ backgroundImage: `url(${brand.heroImage})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <span className="text-xs uppercase tracking-wider text-gray-300 mb-2">
                    {brand.country}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-medium text-white mb-2">
                    {brand.name}
                  </h3>
                  <p className="text-gray-300 mb-4">{brand.specialty}</p>
                  <span className="inline-flex items-center text-white font-medium group-hover:underline">
                    Explore Brand
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-2" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm uppercase tracking-wider text-gray-500 mb-4 block">About Us</span>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                {landingPage?.aboutSectionTitle || 'Your Partner in'}
                <span className="block font-medium">{landingPage?.aboutSectionTitleHighlight || 'Architectural Lighting'}</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {company?.description}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                {landingPage?.aboutSectionText || "Our portfolio includes some of the world's most innovative lighting brands, each selected for their commitment to quality, design excellence, and sustainability."}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-gray-900 font-medium hover:underline"
              >
                Learn More About Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                {brands[0]?.heroImage && (
                  <img
                    src={brands[0].heroImage}
                    alt="Lighting project"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
                {brands[1]?.heroImage && (
                  <img
                    src={brands[1].heroImage}
                    alt="Lighting project"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                )}
              </div>
              <div className="space-y-4 pt-8">
                {brands[2]?.heroImage && (
                  <img
                    src={brands[2].heroImage}
                    alt="Lighting project"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                )}
                {brands[3]?.heroImage && (
                  <img
                    src={brands[3].heroImage}
                    alt="Lighting project"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-sm uppercase tracking-wider text-gray-500 mb-4 block">Latest Updates</span>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900">Mesh News</h2>
            </div>
            <Link
              to="/news"
              className="hidden md:inline-flex items-center text-gray-900 font-medium hover:underline"
            >
              View All News
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.slice(0, 3).map((item) => (
              <article key={item.id} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {item.category}
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{item.date}</span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                    {item.title}
                  </h3>
                  <RichTextContent preview content={item.excerpt} className="text-gray-600 text-sm line-clamp-2 [&_*]:my-0 [&_p]:text-inherit [&_*]:text-inherit" testId={`home-news-excerpt-${item.id}`} />
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link
              to="/news"
              className="inline-flex items-center text-gray-900 font-medium hover:underline"
            >
              View All News
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Logos Strip */}
      <section className="py-16 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to={`/brand/${brand.id}`}
                className="group flex flex-col items-center"
              >
                <span className="text-xl font-medium text-gray-400 group-hover:text-gray-900 transition-colors">
                  {brand.name}
                </span>
                <span className="text-xs text-gray-400 mt-1">{brand.country}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            {landingPage?.ctaSectionTitle || 'Ready to illuminate your next project?'}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-10">
            {landingPage?.ctaSectionText || "Let's discuss how our portfolio of world-leading lighting brands can bring your vision to life."}
          </p>
          <Link
            to={landingPage?.ctaSectionButtonLink || '/contact'}
            className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-medium rounded-sm hover:bg-gray-100 transition-colors"
          >
            {landingPage?.ctaSectionButtonText || 'Contact Us'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
